import API from "../../helpers/network/http";
import {
    applyOnboardingAction,
    createDefaultOnboardingV2Preferences,
    DeepPartial,
    OnboardingActionEntry,
    OnboardingActionId,
    OnboardingV2Preferences,
    validateOnboardingRoute,
} from "./onboarding";
import {
    createDefaultModuleHelpPreferences,
    ModuleHelpPreferencesPayload,
} from "./module_help";

interface UserConfigResponse {
    preference?: Record<string, unknown>;
    preferences?: Record<string, unknown>;
}

const ONBOARDING_V2_PREF_KEY = "onboarding_v2";
const MODULE_HELP_PREF_KEY = "module_help";

function dispatchWindowEvent(name: string, detail: unknown) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(name, {detail}));
}

function normalizePreferences(payload: UserConfigResponse | null | undefined): Record<string, unknown> {
    if (!payload) return {};
    if (payload.preferences && typeof payload.preferences === "object") return payload.preferences;
    if (payload.preference && typeof payload.preference === "object") return payload.preference;
    return {};
}

function deepMerge(base: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
    if (Array.isArray(patch)) return patch;
    if (!patch || typeof patch !== "object") return patch;

    const output = {...(base && typeof base === "object" ? base : {})} as Record<string, unknown>;
    Object.entries(patch).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            output[key] = value;
            return;
        }
        if (value && typeof value === "object") {
            const existing = output[key];
            output[key] = deepMerge(
                existing && typeof existing === "object" && !Array.isArray(existing) ? existing as Record<string, unknown> : {},
                value as Record<string, unknown>
            );
            return;
        }
        output[key] = value;
    });

    return output;
}

export default class OnboardingService {
    static async getRawUserPreferences(): Promise<Record<string, unknown>> {
        const {data} = await API.get("/config/user");
        return normalizePreferences(data?.data);
    }

    static async getOnboardingV2Preferences(): Promise<OnboardingV2Preferences> {
        const prefs = await this.getRawUserPreferences();
        const onboarding = prefs?.[ONBOARDING_V2_PREF_KEY];
        const normalized = createDefaultOnboardingV2Preferences(
            onboarding && typeof onboarding === "object" ? onboarding : {}
        );
        normalized.recommended_route = validateOnboardingRoute(normalized.recommended_route);
        return normalized;
    }

    static async saveOnboardingV2Preferences(payload: OnboardingV2Preferences): Promise<void> {
        await API.put("/config/user", {
            preferences: {
                [ONBOARDING_V2_PREF_KEY]: payload,
            },
        });
        dispatchWindowEvent("vc:onboarding-preferences-updated", payload);
    }

    static async patchOnboardingV2Preferences(patch: DeepPartial<OnboardingV2Preferences>): Promise<OnboardingV2Preferences> {
        const current = await this.getOnboardingV2Preferences();
        const merged = deepMerge(
            current as unknown as Record<string, unknown>,
            patch as unknown as Record<string, unknown>
        );
        const next = createDefaultOnboardingV2Preferences(merged as DeepPartial<OnboardingV2Preferences>);
        next.recommended_route = validateOnboardingRoute(next.recommended_route);
        await this.saveOnboardingV2Preferences(next);
        return next;
    }

    static async resumeOnboarding(): Promise<OnboardingV2Preferences> {
        return this.patchOnboardingV2Preferences({
            dismissed: false,
            steps: {
                dismissed_at: null,
            },
        });
    }

    static async dismissOnboarding(): Promise<OnboardingV2Preferences> {
        return this.patchOnboardingV2Preferences({
            dismissed: true,
            steps: {
                dismissed_at: new Date().toISOString(),
            },
        });
    }

    static async recordMeaningfulAction(
        actionId: OnboardingActionId,
        source: OnboardingActionEntry["source"] = "analytics"
    ): Promise<OnboardingV2Preferences> {
        const current = await this.getOnboardingV2Preferences();
        if (current.actions.completed.includes(actionId)) {
            return current;
        }
        const next = applyOnboardingAction(current, actionId, source);
        await this.saveOnboardingV2Preferences(next);
        dispatchWindowEvent("vc:onboarding-action-recorded", next);
        return next;
    }

    static async reconcileMeaningfulActions(
        actionIds: OnboardingActionId[],
        source: OnboardingActionEntry["source"] = "fallback"
    ): Promise<OnboardingV2Preferences> {
        const requestedActions = Array.from(new Set(actionIds));
        const current = await this.getOnboardingV2Preferences();

        if (requestedActions.length === 0) {
            return current;
        }

        const missingActions = requestedActions.filter((actionId) => !current.actions.completed.includes(actionId));
        if (missingActions.length === 0) {
            return current;
        }

        const next = missingActions.reduce(
            (preferences, actionId) => applyOnboardingAction(preferences, actionId, source),
            current
        );

        await this.saveOnboardingV2Preferences(next);
        dispatchWindowEvent("vc:onboarding-action-recorded", next);
        return next;
    }

    static async getModuleHelpPreferences(): Promise<ModuleHelpPreferencesPayload> {
        const prefs = await this.getRawUserPreferences();
        const moduleHelp = prefs?.[MODULE_HELP_PREF_KEY];

        if (!moduleHelp || typeof moduleHelp !== "object") {
            return createDefaultModuleHelpPreferences();
        }
        const moduleHelpObject = moduleHelp as Record<string, unknown>;

        return {
            version: Number.isFinite(Number(moduleHelpObject.version)) ? Number(moduleHelpObject.version) : 1,
            modules: moduleHelpObject.modules && typeof moduleHelpObject.modules === "object" ? moduleHelpObject.modules : {},
        };
    }

    static async saveModuleHelpPreferences(payload: ModuleHelpPreferencesPayload): Promise<void> {
        await API.put("/config/user", {
            preferences: {
                [MODULE_HELP_PREF_KEY]: payload,
            },
        });
        dispatchWindowEvent("vc:module-help-preferences-updated", payload);
    }
}
